import { useEffect, useState } from 'react';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import type { DataSubscription } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { useOrgFeature } from '../../lib/orgFeatures';

/**
 * SIL-2055: read-only "Data subscription" panel (tier, limits, expiry).
 * Subscriptions are set by SiliconX ops; there is no customer write path.
 * Renders nothing for orgs without the `dataProduct` feature or without a
 * subscription row.
 */
export function DataSubscriptionPanel() {
  const enabled = useOrgFeature('dataProduct');
  const [sub, setSub] = useState<DataSubscription | null>(null);

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      const res = await api.api.client['data-subscription'].$get();
      await throwIfNotOk(res);
      const body = (await res.json()) as unknown as { subscription: DataSubscription | null };
      setSub(body.subscription);
    })().catch(() => setSub(null));
  }, [enabled]);

  if (!enabled || !sub) return null;

  const fmt = (iso: string) => new Date(iso).toLocaleDateString();
  return (
    <Card className="space-y-4">
      <PanelTitle
        title="Data subscription"
        description="Your SiliconX data product plan. Contact SiliconX to change it."
      />
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Tier</dt>
          <dd className="mt-0.5 text-sm text-foreground">{sub.tier}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Status</dt>
          <dd className="mt-0.5 text-sm text-foreground">{sub.status}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Limits</dt>
          <dd className="mt-0.5 text-sm text-foreground">
            {sub.rateLimitPerMin} calls/min, {sub.dailyQuota} calls/day
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Starts</dt>
          <dd className="mt-0.5 text-sm text-foreground">{fmt(sub.startsAt)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-muted-foreground">Expires</dt>
          <dd className="mt-0.5 text-sm text-foreground">{sub.endsAt ? fmt(sub.endsAt) : 'No expiry'}</dd>
        </div>
      </dl>
    </Card>
  );
}
